import { CloseOutlined, DownloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Input, Segmented, Select, Table, Tabs, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { hasBillingAccess, hasCapability, hasSalesAccess } from '../access';
import { formatDate, formatINR, parseDay, todayISO } from '../format';
import { useZenBootstrap } from '../hooks';
import { OrderPeek } from '../OrderPeek';
import { loadDensity, saveDensity, type Density } from '../preferences';
import { orderStatus } from '../status';
import type { WorkOrder } from '../types';
import { DueText, EmptyState, OrderNumber, StatusPill, TableSkeleton } from '../ui';
import { ORDER_VIEWS, isOrderViewKey, orderValue, ordersForView, scopeOrdersForUser, type OrderViewKey } from '../views';
import { filterWorkOrders, type WorkOrderDashboardFilters } from '../workOrderDashboard';

const { Text } = Typography;

const EMPTY_COPY: Record<OrderViewKey, { title: string; description: string }> = {
  active: { title: 'No active work orders', description: 'New work orders appear here as soon as they are created.' },
  'awaiting-invoice': { title: 'Nothing waiting on Accounts', description: 'Every active order has an invoice.' },
  'due-week': { title: 'Nothing due this week', description: 'Orders due in the next seven days appear here.' },
  overdue: { title: 'Nothing is late', description: 'Orders past their delivery date appear here.' },
  ready: { title: 'Nothing ready for delivery', description: 'Orders appear here once every department has finished production.' },
  mine: { title: 'You haven’t created any active orders', description: 'Orders you create appear here.' },
  completed: { title: 'No completed work orders', description: 'Orders move here when every task is done.' },
  deleted: { title: 'No deleted work orders', description: 'Deleted orders stay here with their history.' },
};

export function OrdersPage({ view: routeView = 'active' }: { view?: 'active' | 'completed' | 'deleted' }) {
  const { data, isLoading } = useZenBootstrap();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [peekId, setPeekId] = useState<string | null>(null);
  const billing = data ? hasBillingAccess(data.currentUser) : false;
  const [density, setDensity] = useState<Density>(() => loadDensity(billing ? 'compact' : 'comfortable'));

  const paramView = params.get('view');
  const view: OrderViewKey = isOrderViewKey(paramView) ? paramView : routeView;
  const filters: WorkOrderDashboardFilters = {
    query: params.get('q') ?? '',
    clientId: params.get('client') ?? '',
    dateFrom: params.get('from') ?? '',
    dateTo: params.get('to') ?? '',
    status: 'all',
  };

  const scoped = useMemo(() => {
    if (!data) return [];
    const user = data.currentUser;
    // Sales users without Administrator access see the orders they created (unchanged rule).
    return scopeOrdersForUser(data.workOrders, user, hasSalesAccess(user) && !user.isAdmin);
  }, [data]);

  if (isLoading || !data) return <TableSkeleton />;
  const user = data.currentUser;
  const views = ORDER_VIEWS.filter((item) => !item.adminOnly || user.isAdmin);
  const inView = ordersForView(scoped, view, user);
  const orders = filterWorkOrders(inView, filters);
  const clients = [...new Map(scoped.map((order) => [order.clientId, { value: order.clientId, label: order.customerName }])).values()]
    .sort((left, right) => left.label.localeCompare(right.label));
  const peekOrder = data.workOrders.find((order) => order.id === peekId) ?? null;
  const totalValue = orders.reduce((sum, order) => sum + (orderValue(order) ?? 0), 0);

  const setParam = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setParams(next, { replace: true });
  };
  const changeView = (key: string) => {
    if (key === 'completed') navigate({ pathname: '/work-orders/completed' });
    else if (key === 'deleted') navigate({ pathname: '/work-orders/deleted' });
    else navigate({ pathname: '/work-orders', search: key === 'active' ? '' : `?view=${key}` });
  };
  const changeDensity = (value: Density) => {
    setDensity(value);
    saveDensity(value);
  };

  const activeChips = [
    filters.query && { key: 'q', label: `“${filters.query}”` },
    filters.clientId && { key: 'client', label: clients.find((client) => client.value === filters.clientId)?.label ?? 'Customer' },
    filters.dateFrom && { key: 'from', label: `From ${formatDate(filters.dateFrom)}` },
    filters.dateTo && { key: 'to', label: `To ${formatDate(filters.dateTo)}` },
  ].filter(Boolean) as Array<{ key: string; label: string }>;

  const exportView = async () => {
    const { exportXlsx } = await import('../../lib/exportXlsx');
    const headers = ['Work order', 'Customer', 'Items', 'Status', 'Due', 'Created by', ...(billing ? ['Value', 'Customer balance'] : [])];
    const rows = orders.map((order) => [
      order.orderNumber,
      order.customerName,
      order.items.map((item) => item.description.split('\n')[0]).join(' | '),
      orderStatus(order).label,
      order.deliveryDate,
      order.creatorName,
      ...(billing ? [orderValue(order) ?? '', order.customerBalance ?? ''] : []),
    ]);
    exportXlsx(`work-orders-${view}`, headers, rows);
  };

  const columns: ColumnsType<WorkOrder> = [
    {
      title: 'Order',
      key: 'order',
      width: 170,
      sorter: (left, right) => left.orderNumber.localeCompare(right.orderNumber),
      render: (_, order) => <OrderNumber>{order.orderNumber}</OrderNumber>,
    },
    {
      title: 'Client · item',
      key: 'client',
      sorter: (left, right) => left.customerName.localeCompare(right.customerName),
      render: (_, order) => (
        <div className="ua-cell-two-line">
          <Text strong>{order.customerName}</Text>
          <Text type="secondary" ellipsis>
            {order.items[0]?.description.split('\n')[0] ?? ''}
            {order.items.length > 1 ? ` · +${order.items.length - 1} more` : ''}
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 180,
      render: (_, order) => <StatusPill status={orderStatus(order)} />,
    },
    {
      title: 'Due',
      key: 'due',
      width: 120,
      defaultSortOrder: view === 'completed' || view === 'deleted' ? undefined : 'ascend',
      sorter: (left, right) => parseDay(left.deliveryDate).getTime() - parseDay(right.deliveryDate).getTime(),
      render: (_, order) => <DueText date={order.deliveryDate} done={order.status !== 'active' || Boolean(order.deletedAt)} />,
    },
    { title: 'Created by', dataIndex: 'creatorName', width: 150, ellipsis: true, responsive: ['xl'] },
    ...(billing ? [
      {
        title: 'Value',
        key: 'value',
        align: 'right' as const,
        width: 130,
        sorter: (left: WorkOrder, right: WorkOrder) => (orderValue(left) ?? 0) - (orderValue(right) ?? 0),
        render: (_: unknown, order: WorkOrder) => {
          const value = orderValue(order);
          return value === null ? <Text type="secondary">—</Text> : formatINR(value);
        },
      },
      {
        title: 'Balance',
        key: 'balance',
        align: 'right' as const,
        width: 130,
        render: (_: unknown, order: WorkOrder) => {
          const balance = order.customerBalance;
          if (balance === undefined || balance <= 0) return <Text type="secondary">—</Text>;
          return <span className="ua-text-warning">{formatINR(balance)}</span>;
        },
      },
    ] : []),
  ];

  const empty = EMPTY_COPY[view];
  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="flex-end" gap={12} wrap>
        <div>
          <div className="ua-overline">{views.find((item) => item.key === view)?.label ?? 'Orders'}</div>
          <div className="ua-view-total">
            {orders.length} {orders.length === 1 ? 'order' : 'orders'}
            {billing && totalValue > 0 && <span className="ua-view-total-value"> · {formatINR(totalValue)}</span>}
          </div>
        </div>
        <Flex gap={8} wrap>
          <Button icon={<DownloadOutlined />} disabled={!orders.length} onClick={exportView}>Export</Button>
          {hasCapability(user, data.departments, 'create-order') && (
            <Link to="/work-orders/new"><Button type="primary" icon={<PlusOutlined />}>New work order</Button></Link>
          )}
        </Flex>
      </Flex>

      <Tabs
        className="ua-view-tabs"
        activeKey={view}
        onChange={changeView}
        items={views.map((item) => ({
          key: item.key,
          label: (
            <span>
              {item.label}
              <span className="ua-tab-count">{ordersForView(scoped, item.key, user).length}</span>
            </span>
          ),
        }))}
      />

      <Flex className="ua-filter-bar" gap={8} wrap align="center">
        <Input
          aria-label="Search work orders"
          prefix={<SearchOutlined />}
          placeholder="Search order, customer or details"
          value={filters.query}
          onChange={(event) => setParam({ q: event.target.value })}
          allowClear
          className="ua-filter-search"
          data-shortcut-search
        />
        <Select
          aria-label="Filter by customer"
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Customer"
          value={filters.clientId || undefined}
          options={clients}
          onChange={(value) => setParam({ client: value ?? '' })}
          className="ua-filter-select"
          popupMatchSelectWidth={false}
        />
        <Input aria-label="Order date from" type="date" value={filters.dateFrom} onChange={(event) => setParam({ from: event.target.value })} className="ua-filter-date" />
        <Input aria-label="Order date to" type="date" value={filters.dateTo} onChange={(event) => setParam({ to: event.target.value })} className="ua-filter-date" />
        <Segmented
          className="ua-density"
          value={density}
          onChange={(value) => changeDensity(value as Density)}
          options={[{ value: 'comfortable', label: 'Comfortable' }, { value: 'compact', label: 'Compact' }]}
        />
      </Flex>
      {activeChips.length > 0 && (
        <Flex gap={6} wrap align="center">
          {activeChips.map((chip) => (
            <button key={chip.key} type="button" className="ua-chip ua-chip--active" onClick={() => setParam({ [chip.key]: '' })}>
              {chip.label} <CloseOutlined aria-label="Remove filter" />
            </button>
          ))}
          <Button type="link" size="small" onClick={() => setParam({ q: '', client: '', from: '', to: '' })}>Clear all</Button>
        </Flex>
      )}

      <Card className="ua-table-card" styles={{ body: { padding: 0 } }}>
        <Table
          className={`zen-responsive-table ua-orders-table ua-density-${density}`}
          rowKey="id"
          size={density === 'compact' ? 'small' : 'middle'}
          dataSource={orders}
          columns={columns}
          scroll={{ x: 'max-content' }}
          sticky
          pagination={orders.length > 50 ? { pageSize: 50, showSizeChanger: false } : false}
          rowClassName={(order) => [
            order.id === peekId ? 'is-selected' : '',
            !order.deletedAt && order.status === 'active' && order.deliveryDate < todayISO() ? 'is-late' : '',
          ].join(' ')}
          onRow={(order) => ({
            onClick: (event) => {
              if (event.metaKey || event.ctrlKey) navigate(`/work-orders/${order.id}`);
              else setPeekId(order.id);
            },
            onKeyDown: (event) => {
              if (event.key === 'Enter') setPeekId(order.id);
            },
            tabIndex: 0,
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: <EmptyState title={activeChips.length ? 'No orders match these filters' : empty.title} description={activeChips.length ? 'Remove a filter to see more.' : empty.description} /> }}
        />
      </Card>

      <OrderPeek order={peekOrder} user={user} onClose={() => setPeekId(null)} />
    </Flex>
  );
}
